import { createFeature, createReducer, on } from '@ngrx/store';
import { UserModel } from '@rillion/auth/models';
import { RoleAggregate } from '../../../libs/role';
import { LicenceModel } from '../../../libs/system';
import { AuthActions } from './auth.actions';
import { LoadingEntity } from '@rillion/shared/models/loading-state';

export interface AuthState {
  user: LoadingEntity<UserModel>;
  roles: LoadingEntity<string[]>;
  licences: LoadingEntity<LicenceModel[]>;
  permissions: LoadingEntity<RoleAggregate[]>;
}

export const authInitialState: AuthState = {
  user: LoadingEntity.initial(),
  roles: LoadingEntity.initial(),
  licences: LoadingEntity.initial(),
  permissions: LoadingEntity.initial(),
};

export const authFeature = createFeature({
  name: 'auth',
  reducer: createReducer(
    authInitialState,

    // Get user
    on(
      AuthActions.getUser,
      (state): AuthState => ({
        ...state,
        user: LoadingEntity.loading(),
      }),
    ),
    on(
      AuthActions.getUserSuccess,
      (state, { user }): AuthState => ({
        ...state,
        user: LoadingEntity.loaded(new UserModel(user)),
      }),
    ),
    on(
      AuthActions.updateUserRole,
      (state, { user }): AuthState => ({
        ...state,
        user: LoadingEntity.loaded(new UserModel(user)),
      }),
    ),
    on(
      AuthActions.getUserFailure,
      (state, { error }): AuthState => ({
        ...state,
        user: LoadingEntity.failed(error),
      }),
    ),

    // Delete User (Logout Workaround)
    on(AuthActions.removeUser, (state): AuthState => {
      let { user } = state;
      user = undefined;

      return {
        ...state,
        user: user,
      };
    }),

    // Get roles
    on(
      AuthActions.getRoles,
      (state): AuthState => ({
        ...state,
        roles: LoadingEntity.loading(),
      }),
    ),
    on(
      AuthActions.getRolesSuccess,
      (state, { roles }): AuthState => ({
        ...state,
        roles: LoadingEntity.loaded(roles),
      }),
    ),
    on(
      AuthActions.getRolesFailure,
      (state, { error }): AuthState => ({
        ...state,
        roles: LoadingEntity.failed(error),
      }),
    ),

    // License
    on(
      AuthActions.getLicences,
      (state): AuthState => ({
        ...state,
        licences: LoadingEntity.loading(),
      }),
    ),
    on(
      AuthActions.getLicencesSuccess,
      (state, { licences }): AuthState => ({
        ...state,
        licences: LoadingEntity.loaded(licences),
      }),
    ),
    on(
      AuthActions.getLicencesFailure,
      (state, { error }): AuthState => ({
        ...state,
        licences: LoadingEntity.failed(error),
      }),
    ),

    // Permissions
    on(
      AuthActions.getPermissions,
      (state): AuthState => ({
        ...state,
        permissions: LoadingEntity.loading(),
      }),
    ),
    on(
      AuthActions.getPermissionsSuccess,
      (state, { permissions }): AuthState => ({
        ...state,
        permissions: LoadingEntity.loaded(permissions),
      }),
    ),
    on(
      AuthActions.getPermissionsFailure,
      (state, { error }): AuthState => ({
        ...state,
        permissions: LoadingEntity.failed(error),
      }),
    ),

    // Set user role
    on(
      AuthActions.setUserRole,
      (state, { role }): AuthState => ({
        ...state,
        user: LoadingEntity.loaded({ ...state.user.entity, role }),
      }),
    ),
  ),
});
